import { Input } from "@/components/ui/input"
import { useNavigate } from "react-router-dom"
import CabBookingCard from "@/pages/cab-bookings/components/CabBookingCard"
import {
  useFetchCabBookings,
  useDeleteCabBooking,
  useRejectCabBooking,
} from "@/services/query/cabBookings.query"
import { useState, useCallback } from "react"
import { useToast } from "@/components/ui/use-toast"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { Button } from "@/components/ui/button"

function CabBookingsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [pagination, setPagination] = useState({ offset: 0, limit: 9 })
  const [search, setSearch] = useState("")
  const [orderStatus, setOrderStatus] = useState(null)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)

  // Debounce search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPagination((prev) => ({ ...prev, offset: 0 })) // Reset to first page on search
    }, 500),
    []
  )

  const { data, isLoading, isError, error } = useFetchCabBookings(
    pagination.offset,
    pagination.limit,
    search,
    orderStatus
  )

  const deleteBookingMutation = useDeleteCabBooking()
  const rejectBookingMutation = useRejectCabBooking()

  const handleDelete = async () => {
    if (!selectedBooking) return

    try {
      await deleteBookingMutation.mutateAsync(selectedBooking.id)
      toast({
        title: "Success",
        description: "Booking deleted successfully",
      })
      setDeleteDialogOpen(false)
      setSelectedBooking(null)
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete booking",
        variant: "destructive",
      })
    }
  }

  const handleReject = async (booking) => {
    try {
      await rejectBookingMutation.mutateAsync(booking.id)
      toast({
        title: "Success",
        description: "Booking rejected successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to reject booking",
        variant: "destructive",
      })
    }
  }

  // Update search handler
  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value)
  }

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }))
  }

  const handleCardClick = (bookingId) => {
    if (bookingId) {
      navigate(`/cab-bookings/${bookingId}`)
    }
  }

  return (
    <div className="container mx-auto py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex-1 max-w-sm">
          <Input
            type="search"
            placeholder="Search cab bookings..."
            onChange={handleSearchChange}
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={orderStatus === 0 ? "default" : "outline"}
            className={`${
              orderStatus === 0
                ? "bg-blue-500 text-white hover:bg-blue-600"
                : "hover:bg-blue-50"
            }`}
            onClick={() => setOrderStatus(0)}
          >
            Pending
          </Button>
          <Button
            variant={orderStatus === 1 ? "default" : "outline"}
            className={`${
              orderStatus === 1
                ? "bg-green-500 text-white hover:bg-green-600"
                : "hover:bg-green-50"
            }`}
            onClick={() => setOrderStatus(1)}
          >
            Approved
          </Button>
          <Button
            variant={orderStatus === 2 ? "default" : "outline"}
            className={`${
              orderStatus === 2
                ? "bg-red-500 text-white hover:bg-red-600"
                : "hover:bg-red-50"
            }`}
            onClick={() => setOrderStatus(2)}
          >
            Rejected
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {isLoading && (
          <div className="col-span-3 text-center py-8">
            Loading cab bookings...
          </div>
        )}
        {isError && (
          <div className="col-span-3 text-center text-red-500 py-8">
            Error: {error.message}
          </div>
        )}
        {data?.cabBookings?.map((booking) => (
          <CabBookingCard
            key={booking.id}
            booking={booking}
            onDelete={() => {
              setSelectedBooking(booking)
              setDeleteDialogOpen(true)
            }}
            onReject={() => handleReject(booking)}
            onClick={handleCardClick}
          />
        ))}
        {data?.cabBookings?.length === 0 && (
          <div className="col-span-3 text-center text-gray-500 py-8">
            No cab bookings found
          </div>
        )}
      </div>

      {data && (
        <div className="mt-8 flex justify-center">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    handlePageChange(
                      Math.max(0, pagination.offset - pagination.limit)
                    )
                  }
                  disabled={pagination.offset === 0}
                />
              </PaginationItem>
              {[...Array(data.pagination.totalPages)].map((_, idx) => (
                <PaginationItem key={idx}>
                  <PaginationLink
                    onClick={() => handlePageChange(idx * pagination.limit)}
                    isActive={pagination.offset === idx * pagination.limit}
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    handlePageChange(pagination.offset + pagination.limit)
                  }
                  disabled={
                    pagination.offset + pagination.limit >=
                    data.pagination.total
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              booking.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default CabBookingsPage
